<template>
  <div class="bg-white text-center block gt-sm">
    <div class="component-header">
      <h2 class="heading">
        Designed for <span class="text-blue">Interprofessional Education</span>
      </h2>
      <p class="paragraph">
        Learn how ChartFlow can be incorporated into any healthcare education curriculum.
      </p>
    </div>
    <div class="wrapper">
      <!-- 1 -->
      <div class="container">
        <div class="left-block">
          <div class="left-content">
            <div class="text-left">
              <h3 class="sub-header text-primary">Simulation</h3>
              <p class="sub-para">
                Unfolding Scenarios, Time Control, and Quick Reset between groups puts your SimOps team in the driver's seat.
              </p>
              <div class="bullet-points text-body1">
                <ul>
                  <li>
                    Pause, fast-forward, and quickly reset patient charts between groups.
                  </li>
                  <li>
                    Drop in new orders, labs, vital signs, and more using our Events feature.
                  </li>
                  <li>
                    Build your own patients or copy & edit one of our 100+ pre-built scenarios.
                  </li>
                  <li>
                    Sign-in made easy with support for ID Cards, Barcodes, and Single Sign-On (SSO) via SAML.
                  </li>
                </ul>
              </div>
              <div class="program-actions">
                <q-btn
                  v-for="program in programsFor(['simulation'])"
                  :key="program.slug"
                  class="get-started-free text-white bg-secondary text-bold"
                  :label="`Learn more about ${program.name}`"
                  :to="`/programs/${program.slug}`"
                />
              </div>
            </div>
          </div>
        </div>
        <div class="right-block">
          <div class="dashed-vertical-line"></div>
          <div class="right-content">
            <div class="img-wrapper">
              <q-img
                class="image-with-shadow"
                src="/assets/homePage/chartflow-simulation.png"
                alt="ChartFlow simulation interface showing real-time patient monitoring and control panel"
              />
            </div>
          </div>
        </div>
      </div>
      <hr class="horizontal-line" />
      <!-- 2 -->
      <div class="container">
        <div class="left-block">
          <div class="dashed-vertical-line"></div>
          <div class="left-content">
            <div class="img-wrapper">
              <q-img
                class="image-with-shadow"
                src="/assets/homePage/chartflow-nursing-mar.png"
                alt="ChartFlow nursing eMAR (electronic Medication Administration Record) interface"
              />
            </div>
          </div>
        </div>
        <div class="right-block">
          <div class="right-content">
            <div class="vertical-space"></div>
            <div class="text-left">
              <h3 class="sub-header text-primary">Nursing</h3>
              <p class="sub-para">
                Prepare nursing students at all levels for real-world documentation.
              </p>
              <div class="bullet-points text-body1">
                <ul>
                  <li>
                    Practice medication administration & barcode scanning using our realistic eMAR. Wallcur Practi-Meds & Pocket Nurse
                    Demo Dose medications are supported.
                  </li>
                  <li>
                    Reduce clinical paperwork and collect care plans with New Patient Activities.
                  </li>
                  <li>
                    100+ Pre-built Patient Scenarios — Edit ours or build your own!
                  </li>
                  <li>
                    Over 50 flowsheets & sections, including: Assessments, Vital Signs, Intake & Output, OB, Labs, Screenings, SBAR.
                  </li>
                </ul>
              </div>
              <div class="program-actions">
                <q-btn
                  v-for="program in programsFor(['nursing'])"
                  :key="program.slug"
                  class="get-started-free text-white bg-secondary text-bold"
                  :label="`Learn more about ${program.name}`"
                  :to="`/programs/${program.slug}`"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
      <hr class="horizontal-line" />
      <!-- 3 -->
      <div class="container">
        <div class="left-block">
          <div class="left-content">
            <div class="vertical-space"></div>
            <div class="text-left">
              <h3 class="sub-header text-primary">Medical Assistant</h3>
              <p class="sub-para">
                ChartFlow’s Outpatient layout provides a realistic environment for students to practice reviewing & documenting on a patient
                chart.
              </p>
              <div class="bullet-points text-body1">
                <ul>
                  <li>
                    Practice Appointment Scheduling and Patient Registration.
                  </li>
                  <li>
                    Billing & Coding section incorporates Diagnosis, Procedural, and Billing fields for student practice.
                  </li>
                  <li>
                    Review physician notes, labs, past visits, schedule follow-ups, and more.
                  </li>
                  <li>
                    Two ready-to-use medical assistant courses: 7 CCMA labs with lesson plans, plus 5 self-guided activities with learning modules.
                  </li>
                </ul>
              </div>
              <div class="program-actions">
                <q-btn
                  v-for="program in programsFor(['medical-assistant'])"
                  :key="program.slug"
                  class="get-started-free text-white bg-secondary text-bold"
                  :label="`Learn more about ${program.name}`"
                  :to="`/programs/${program.slug}`"
                />
              </div>
            </div>
          </div>
        </div>
        <div class="right-block">
          <div class="dashed-vertical-line"></div>
          <div class="right-content">
            <div class="img-wrapper">
              <q-img
                class="image-with-shadow"
                src="/assets/homePage/chartflow-medical-assistant.png"
                alt="ChartFlow medical assistant interface showing outpatient layout and appointment scheduling"
              />
            </div>
          </div>
        </div>
      </div>
      <hr class="horizontal-line" />
      <!-- 4 -->
      <div class="container">
        <div class="left-block">
          <div class="dashed-vertical-line"></div>
          <div class="left-content">
            <div class="img-wrapper">
              <q-img
                class="image-with-shadow"
                src="/assets/homePage/chartflow-allied-health-cte.png"
                alt="ChartFlow allied health and CTE interface showing specialty documentation features"
              />
            </div>
          </div>
        </div>
        <div class="right-block">
          <div class="right-content">
            <div class="vertical-space"></div>
            <div class="text-left">
              <h3 class="sub-header text-primary">Allied Health + Career &amp; Technical Education (CTE)</h3>
              <p class="sub-para">
                Realistic documentation practice for allied health and CTE programs, in the same chart the whole care team uses.
              </p>
              <div class="bullet-points text-body1">
                <ul>
                  <li>
                    Different Visit Layouts within ChartFlow's academic EHR provide a realistic simulation for students.
                  </li>
                  <li>
                    Specialty documentation included for Respiratory, Physical Therapy, Occupational Therapy, and Dietetics programs.
                  </li>
                  <li>
                    Activities of Daily Living (ADL), Interventions, and Screenings designed for PCT & CNA programs.
                  </li>
                  <li>
                    ChartFlow's educational EHR is easy to use for all levels —
                    <router-link class="text-secondary" to="/demo">create a free instructor demo account</router-link>!
                  </li>
                </ul>
              </div>
              <div class="program-actions">
                <q-btn
                  v-for="program in programsFor(['allied-health', 'career-technical-education'])"
                  :key="program.slug"
                  class="get-started-free text-white bg-secondary text-bold"
                  :label="`Learn more about ${program.name}`"
                  :to="`/programs/${program.slug}`"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
      <hr class="horizontal-line" />
      <!-- 5 -->
      <div class="container">
        <div class="left-block">
          <div class="left-content">
            <div class="vertical-space"></div>
            <div class="text-left">
              <h3 class="sub-header text-primary">Advanced Implementation</h3>
              <p class="sub-para">
                Launched in 2023, ChartFlow’s modern technology includes powerful features for programs looking to manage multiple campuses,
                programs, and more.
              </p>
              <div class="bullet-points text-body1">
                <ul>
                  <li>
                    Administrator accounts are able to easily manage multiple campuses & programs for Interprofessional Education (IPE).
                  </li>
                  <li>
                    Assign a computer, laptop, or tablet to a simulation activity, and students go straight to it when they sign in.
                  </li>
                  <li>
                    ChartFlow supports integration with Learning Management Systems (LMS) via LTI 1.3.
                  </li>
                  <li>
                    Looking to completely customize your own simulated EHR with custom flowsheets, visit types, and more? Contact our team
                    by emailing <a class="text-secondary" href="mailto:sales@chartflow.io" target="_blank">sales@chartflow.io</a>.
                  </li>
                </ul>
              </div>
              <q-btn class="get-started-free text-white bg-secondary text-bold" label="Free Instructor Demo" to="/demo" />
            </div>
          </div>
        </div>
        <div class="right-block">
          <div class="dashed-vertical-line"></div>
          <div class="right-content">
            <div class="img-wrapper">
              <q-img
                class="image-with-shadow"
                src="/assets/homePage/Advanced Implementation.png"
                alt="ChartFlow advanced implementation dashboard showing multi-campus management features"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { getProgram } from "../../data/marketingPrograms";

defineOptions({
  name: "ExploreBanner",
});

// Program pages that exist for the given slugs, in order.
function programsFor(slugs) {
  return slugs.map(getProgram).filter(Boolean);
}
</script>

<style scoped lang="scss">
.block {
  padding: 3rem;
}

.sub-header {
  font-size: 36px;
  line-height: 43px;
  font-weight: 600;
  padding-bottom: 15px;
  margin: 0;
}

.sub-para {
  font-size: 16px;
  line-height: 26px;
  font-weight: 400;
  color: #4a4a4a; /* Improved contrast from $grey-7 */
  margin: 0;
}

.bullet-points {
  padding: 20px 0;
}

.bullet-points p {
  font-size: 16px;
  font-weight: 500;
  line-height: 12px;
}

.bullet-points ul {
  list-style: none;
  padding-left: 0;
}

.bullet-points li {
  position: relative;
  margin-bottom: 16px;
  padding-left: 26px;
}

.bullet-points li:before {
  position: absolute;
  left: 8px;
  top: 0px;
  content: "\e92d";
  font-family: "Material Icons";
  color: $accent;
}

.get-started-free {
  border-radius: 30px;
  padding: 10px 30px;
  font-size: 14px;
  margin: 0;
}

.program-actions {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.75rem;
}

.container {
  display: flex;
  justify-content: stretch;
  align-items: stretch;
  gap: 4rem;
  padding: 0;
}

.left-block,
.right-block {
  align-self: stretch;
  flex: 1;
  position: relative;
}

.left-content,
.right-content {
  margin: 0;
  border-radius: 8px;
  margin-bottom: 10px;
}

.image-with-shadow {
  box-shadow: 0px 10px 15px #e5eefb;
  border: 3px solid #e5eefb;
  border-radius: 20px;
  display: block;
  margin: 0 auto;
  max-width: 100%;
}

.horizontal-line {
  width: 52%;
  height: 2px;
  margin: 0px auto;
  border: none;
  background: repeating-linear-gradient(
    to right,
    #939393,
    #939393 5px,
    transparent 5px,
    transparent 10px
  );
}

.img-wrapper {
  margin-bottom: 70px;
  margin-top: 70px;
}

.dashed-vertical-line {
  position: absolute;
  width: 2px;
  left: 50%;
  height: 100%;
  background: repeating-linear-gradient(
    to bottom,
    #939393,
    #939393 5px,
    transparent 5px,
    transparent 10px
  );
}

.vertical-space {
  width: 1px;
  height: 70px;
  margin: 0px auto;
}

</style>
